import { nextTick } from "vue";
import { iframeMutations } from "@/shared/store/iframe.mutations";
import { copy } from "@/shared/utils/string";
import { cloneDeep } from "@/shared/utils/helpers.js";
import { EventBus } from "@/shared/projects/event-bus.ts";
import { pageSnapshotService } from "@/apps/page/store/page-snapshot.service";
import { t } from "@/i18n";
import { projectsService } from "@/shared/projects/projects.service";
import { pageUploadService } from "@/apps/page/services/page-upload.service";
import {
    BookIcon,
    Building2Icon,
    BusinessHandshakeIcon,
    CalendarIcon,
    CoinsHandIcon,
    PlayerIcon,
    StoreIcon,
    UserMultipleIcon,
} from "@easy/ui/icons";
import { makeSection, mutateSections } from "@/apps/page/model/make-section";
import { pageSectionsService } from "@/apps/page/store/page-sections.service";
import { pageToolsService } from "@/apps/page/services/page-tools.service";
import { makeSkimmedToolProjectData } from "@/apps/page/project/tool-project-data";

const isNotSectionType = (type) => (section) => section.type !== type;
const isNotDeleted = (obj) => !obj._deleted;
const isCreated = (obj) => obj._created;

export default {
    namespaced: true,
    state: () => ({
        page: {},
        sections: [],
        colorsGhost: {},
        settingsGhost: {},
        iframe: null,
        iframeLoaded: false,
        iframeMinimap: null,
        iframeMinimapLoaded: false,
        hasChanges: false,
        hasSnapshot: false,
        sidebarMode: "list",
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.page,
        pageIframe: (state, g, rootState) => {
            return `<iframe
                id="${state.page.uuid}"
                width="100%"
                src="${rootState.globalUrl}/pages/${state.page.uuid}"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                frameborder="0"
                scrolling="no"
                ></iframe>
                <script> window.addEventListener("message", (e) => {!!e.data.frameHeight && e.data.pageUuid === "${state.page.uuid}" && (document.getElementById("${state.page.uuid}").style.height = e.data.frameHeight);});</script>`;
        },
        templates: () => [
            {
                value: t("love.templates.empty"),
                id: "empty",
            },
            {
                value: t("love.templates.product"),
                icon: StoreIcon,
                id: "product",
            },
            {
                value: "E-book",
                icon: BookIcon,
                id: "e-book",
            },
            {
                value: t("love.templates.training"),
                icon: CalendarIcon,
                id: "training",
            },
            {
                value: t("love.templates.consultations"),
                icon: BusinessHandshakeIcon,
                id: "consultations",
            },
            {
                value: t("love.templates.community"),
                icon: UserMultipleIcon,
                id: "community",
            },
            {
                value: "Webinar",
                icon: PlayerIcon,
                id: "webinar",
            },
            {
                value: t("love.templates.service"),
                icon: CoinsHandIcon,
                id: "service",
            },
            {
                value: t("love.templates.event"),
                icon: Building2Icon,
                id: "event",
            },
        ],
        getSectionById: (state) => (id) => {
            return state.sections.find((x) => x.id === id);
        },
        sectionWebIdLinks: (state) => {
            return (state.sections || [])
                .toSorted((a, b) => a.order - b.order)
                .filter(isNotSectionType("nav"))
                .filter(isNotDeleted)
                .map((s) => ({
                    value: s.webId,
                    label: s?.projectData?.name || s.type,
                }));
        },
        sidebarWidth: (state) => {
            return state.sidebarMode === "addSection" ? 320 : undefined;
        },
        pageColors: (state) => {
            return state.page.colors;
        },
    },
    mutations: {
        ...iframeMutations,

        setProject(state, { page, sections, snapshot = {} }) {
            state.page = pageSnapshotService.makeSnapshotState(state, "page", page, snapshot);
            state.sections = pageSnapshotService.makeSnapshotState(
                state,
                "sections",
                sections,
                snapshot
            );
            state.page.colors = pageSnapshotService.makeSnapshotState(
                state,
                "colorsGhost",
                state.page.colors || {},
                snapshot
            );
            state.page.settings = pageSnapshotService.makeSnapshotState(
                state,
                "settingsGhost",
                state.page.settings || {},
                snapshot
            );
            state.hasChanges = false;
        },
        clearProject(state) {
            state.page = {};
            state.sections = [];
            pageSnapshotService.clearSnapshot(state, "page", {});
            pageSnapshotService.clearSnapshot(state, "sections", []);
            pageSnapshotService.clearSnapshot(state, "colorsGhost", {});
            pageSnapshotService.clearSnapshot(state, "settingsGhost", {});
        },
        addSection(state, section) {
            state.sections.push(makeSection({ ...section }, state.sections.length));
        },
        setSections(state, payload) {
            if (payload) {
                state.sections = pageSnapshotService.makeSnapshotState(
                    state,
                    "sections",
                    payload,
                    {}
                );

                state.hasChanges = true;
            }
            state.sections.filter(isNotDeleted).forEach((s, index) => {
                s.order = index;
                if (!isCreated(s)) {
                    s["_changed"] = true;
                }
            });
        },
    },
    actions: {
        setIframeMinimapLoaded({ state, dispatch }, isLoaded = true) {
            state.iframeMinimap = document.querySelector("#iframe-minimap");
            state.iframeMinimapLoaded = isLoaded;
            dispatch("refreshIframe");
        },
        refreshIframe({ state, getters }) {
            if (!state.iframeLoaded) {
                return;
            }
            const page = cloneDeep(state.page);
            page.sections = cloneDeep(state.sections);
            if (state.iframe && state.iframe.contentWindow && page.id) {
                state.iframe.contentWindow.postMessage({ pageData: page }, getters.appUrl);
            }

            if (!state.iframeMinimapLoaded) {
                return;
            }

            if (state.iframeMinimap && state.iframeMinimap.contentWindow && page.id) {
                state.iframeMinimap.contentWindow.postMessage({ pageData: page }, getters.appUrl);
            }
        },
        createToolSection({ state }, { project, position }) {
            const newSection = makeSection(
                {
                    type: project.tool,
                    pageId: state.page.id,
                    projectId: project.id,
                    projectData: { ...project },
                },
                position ?? state.sections.length,
                "tool"
            );
            mutateSections(state.sections, newSection, position);
            return newSection;
        },
        createBlockSection({ state }, { block, position }) {
            const dataValues = {};
            const styleValues = {};
            block.dataFields.forEach((field) => {
                dataValues[field.value] = field.defaultData;
            });
            block.styleFields.forEach((field) => {
                styleValues[field.value] = field.default;
            });

            const newSection = makeSection(
                {
                    dataValues,
                    styleValues,
                    type: block.type,
                    typeId: block.typeId,
                    pageId: state.page.id,
                },
                position ?? state.sections.length
            );

            mutateSections(state.sections, newSection, position);
            return newSection;
        },
        removeBlockSection({ state }, sectionId) {
            const section = state.sections.find((x) => x.id === sectionId);
            const index = state.sections.indexOf(section);
            if (index > -1) {
                state.sections.splice(index, 1);
            }
        },
        changeBlockSection({ state }, payload) {
            const updatedBlock = Object.assign({}, payload);
            const section = state.sections.find((x) => x.id === payload.id);
            // TODO check if this works (we do not use blocks anymore):
            if (section) {
                Object.assign(section, updatedBlock);
            }
        },
        changeSection({ state }, payload) {
            const updatedSection = Object.assign({}, payload);
            const index = state.sections.findIndex((x) => x.id === payload.id);
            state.sections[index] = updatedSection;
        },
        hideBlockElement({ state }, payload) {
            const section = state.sections.find((x) => x.id === payload.blockId);
            section.dataValues[payload.fieldName].isHidden = true;
        },
        toggleHideSection({ state }, id) {
            const section = state.sections.find((x) => x.id === id);
            if (section) {
                section.isHidden = !section.isHidden;
                if (!isCreated(section) && isNotDeleted(section)) {
                    section["_changed"] = true;
                }
            }
        },
        setOrderSections({ state }, sectionIds) {
            state.sections.forEach((section) => {
                section.order = sectionIds.indexOf(section.id);
                if (!isCreated(section) && isNotDeleted(section)) {
                    section["_changed"] = true;
                }
            });
        },
        setSidebarMode({ state }, mode) {
            state.sidebarMode = mode;
        },
        async removeSection({ state, dispatch }, sectionId) {
            const action = await dispatch(
                "modals/showConfirmModal",
                {
                    title: t("page.sections.remove_title"),
                    description: t("page.sections.remove_message"),
                    ctaLabel: t("shared.delete"),
                    cancelLabel: t("shared.cancel"),
                },
                { root: true }
            );

            if (!action) {
                return;
            }

            const section = state.sections.find((x) => x.id === sectionId);

            if (section._created) {
                const index = state.sections.indexOf(section);
                if (index > -1) {
                    state.sections.splice(index, 1);
                }
            } else {
                delete section._changed;
                section["_deleted"] = true;
            }
        },
        showNoSectionsModal({ commit }) {
            commit(
                "showModal",
                {
                    type: "info",
                    title: t("page.empty.title"),
                    desc: t("page.empty.desc"),
                    cancelBtn: t("shared.cancel"),
                    actionBtn: t("page.empty.cta"),
                    callback: () => {
                        EventBus.$emit("openSidebar", { panel: "data" });
                        setTimeout(() => EventBus.$emit("createSection"), 100);
                    },
                },
                { root: true }
            );
        },
        publishProject({ state, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            if (!state.sections.length) {
                dispatch("showNoSectionsModal");
                return;
            }

            dispatch("hostCode");
        },
        async unpublishPage({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.page.uuid });
            commit("setUnpublished", state.page, { root: true });
            dispatch("page/projects/mergeSnapshotChanges", null, { root: true });
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        async unpublish({ _ }, { uuid }) {
            return projectsService.publish({ tool: "page", uuid, publish: false });
        },
        showLinkModal({ state, getters, commit }) {
            const modalData = {
                type: "code",
                title: t("page.link_modal.title"),
                code: getters.pageIframe,
                actionBtn: t("page.link_modal.copy_code"),
                cancelBtn: t("page.link_modal.copy_link"),
                callback: () => {
                    copy(getters.pageIframe);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("page.link_modal.code_copied"),
                        },
                        { root: true }
                    );
                },
                cancelCallback: () => {
                    copy(`${window.location.origin}/pages/${state.page.uuid}`);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("page.link_modal.link_copied"),
                        },
                        { root: true }
                    );
                },
            };
            commit("showModal", modalData, { root: true });
        },
        async hostCode({ state, rootState, commit, rootGetters, dispatch }) {
            rootState.waiting = true;
            const firstPublish = !rootGetters.isPublished(state.page);

            await nextTick();

            try {
                await projectsService.publish({
                    tool: "page",
                    uuid: state.page.uuid,
                    publish: true,
                });

                commit("setPublished", state.page, { root: true });
                dispatch("page/projects/mergeSnapshotChanges", null, { root: true });
                if (firstPublish) {
                    dispatch("showLinkModal");
                }
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        moveSection({ state, commit }, { sectionId, direction }) {
            const sections = cloneDeep(state.sections);
            const section = sections.find((x) => x.id === sectionId);
            if (!section) {
                return;
            }
            const index = sections.indexOf(section);
            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= state.sections.length) {
                return;
            }
            sections.splice(index, 1);
            sections.splice(newIndex, 0, section);
            commit("setSections", sections);
        },
        async uploadImage({ state, rootState }, payload) {
            rootState.waiting = true;
            try {
                const { file } = await pageUploadService.pageImage({
                    image: payload.file,
                    pageId: state.page.id,
                });

                rootState.waiting = false;
                return file;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async magicallyExchangeContent({ state }, { blockId, fieldName }) {
            const { sections, page } = state;
            const section = sections.find(({ id }) => id === blockId);
            if (section) {
                // field might be an array, so deal with that:
                const [realName] = fieldName.split("_");
                const fieldValue = section.dataValues?.[realName] || {};
                // if (id) {
                // conceptual exchange of only one item ?
                //     fieldValue = Array.isArray(fieldValue?.data?.items)
                //         ? fieldValue.data.items.find((x) => x.id === id)
                //         : fieldValue;
                // }

                try {
                    const response = await pageSectionsService.exchangeFieldData({
                        uuid: page.uuid,
                        fieldName: realName,
                        fieldValue,
                        sectionId: section.id,
                        theme: {
                            id: page.theme,
                            font: page.fontFamily,
                            colors: page.colors,
                        }, // this theme might be wrong (it should be the one from the section - resolve var() problem ?!)
                    });
                    const { isChanged, fieldValue: value } = response;
                    if (isChanged && section.dataValues?.[realName]) {
                        section.dataValues[realName] = value;
                        section._changed = true;
                    }
                    return true;
                } catch (e) {
                    console.error("Error exchanging field data", e);
                    return false;
                }
            }
            return false;
        },
        async connectToolSectionWithProject(
            { state, dispatch },
            { tool, sectionId, projectName, lang }
        ) {
            try {
                const project = await pageToolsService.createNewProject({
                    tool,
                    sectionId,
                    projectName,
                    lang: lang ? lang : state.page?.lang,
                });
                await dispatch("bindToolSectionWithProject", {
                    sectionId,
                    projectId: project.id,
                    projectData: makeSkimmedToolProjectData(project, tool),
                });
                return project.uuid;
            } catch (e) {
                console.error(e);
                return "";
            }
        },
        async bindToolSectionWithProject(
            { state, dispatch },
            { sectionId, projectId, projectUuid, projectData = {} }
        ) {
            const { sections } = state;
            const section = sections.find((x) => x.id === sectionId);
            if (!section) {
                return "";
            }
            section.projectId = projectId;
            // project data MUST be added to the section, to properly display the section when snapshot exists
            section.projectData = projectData;
            section._changed = true;
            await dispatch("page/projects/saveSnapshot", null, { root: true });
            return projectUuid;
        },
    },
};
